import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { DocumentRegular, HistoryRegular, PeopleRegular, PersonRegular, StorageRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { TeamList } from '../features/teams/TeamList';
import { MemberRow } from '../features/groups/GroupMembers';
import { UserPicker } from '../features/users/UserPicker';
import type { DirectoryUserDto, GroupMemberDto, TeamDetailDto, TeamRole, TeamSummaryDto } from '../api/types';
import '../features/groups/groups.css';
import '../features/teams/teams.css';

function formatBytes(n?: number | null): string {
  if (n == null) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function RemovableList({
  title,
  people,
  busy,
  onRemove,
}: {
  title: string;
  people: GroupMemberDto[];
  busy: boolean;
  onRemove: (m: GroupMemberDto) => void;
}) {
  return (
    <div>
      <div className="grp-sublist-title">
        {title} ({people.length})
      </div>
      {people.length === 0 ? (
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>—</Caption1>
      ) : (
        <div className="grp-sublist">
          {people.map((m, i) => (
            <div key={m.id}>
              {i > 0 ? <div className="grp-member-divider" /> : null}
              <MemberRow member={m} removeDisabled={busy} onRemove={() => onRemove(m)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TeamEditCard({
  detail,
  busy,
  onRemove,
}: {
  detail: TeamDetailDto;
  busy: boolean;
  onRemove: (m: GroupMemberDto, role: TeamRole) => void;
}) {
  return (
    <div className="grp-panel">
      <div className="grp-panel-head">
        <span className="grp-panel-title">{detail.displayName}</span>
        <Badge appearance="tint" color="brand">
          Team
        </Badge>
      </div>

      <span className="tm-impact-row">
        <span className="tm-impact" title="Mitglieder">
          <PeopleRegular />
          {detail.memberCount} Mitglieder
        </span>
        <span className="tm-impact" title="Gäste">
          <PersonRegular />
          {detail.guestCount} Gäste
        </span>
        <span className="tm-impact" title="Dateien">
          <DocumentRegular />
          {detail.fileCount ?? '—'} Dateien
        </span>
        <span className="tm-impact" title="Belegter SharePoint-Speicher">
          <StorageRegular />
          {formatBytes(detail.storageUsedBytes)}
        </span>
        <span className="tm-impact" title="Letzte Aktivität">
          <HistoryRegular />
          {detail.lastActivity ?? '—'}
        </span>
      </span>

      {detail.description ? <Text className="grp-desc">{detail.description}</Text> : null}

      <RemovableList title="Owner" people={detail.owners} busy={busy} onRemove={(m) => onRemove(m, 'owner')} />
      <RemovableList title="Mitglieder" people={detail.members} busy={busy} onRemove={(m) => onRemove(m, 'member')} />
      <RemovableList title="Gäste" people={detail.guests} busy={busy} onRemove={(m) => onRemove(m, 'guest')} />
    </div>
  );
}

export function TeamsViewPage() {
  const api = useApi();
  const [params] = useSearchParams();
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const policy = policies.data?.find((p) => p.pageKey === 'teams.members');
  const ticketMode = policy?.ticketMode ?? 'None';
  const approvalRequired = policy?.approvalMode === 'Required';

  const [selected, setSelected] = useState<TeamSummaryDto[]>([]);
  const [details, setDetails] = useState<Record<string, TeamDetailDto>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [ticket, setTicket] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const selectedIds = new Set(selected.map((t) => t.id));

  function toggle(t: TeamSummaryDto) {
    setSelected((prev) => (prev.some((x) => x.id === t.id) ? prev.filter((x) => x.id !== t.id) : [...prev, t]));
  }

  async function loadDetail(id: string) {
    setLoadingIds((s) => new Set(s).add(id));
    try {
      const d = await api.getTeamDetail(id);
      setDetails((m) => ({ ...m, [id]: d }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  }

  useEffect(() => {
    selected.forEach((t) => {
      if (!details[t.id]) void loadDetail(t.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Vorauswahl per ?ids= (Deep-Link aus User-Insights).
  useEffect(() => {
    const ids = (params.get('ids') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) return;
    let active = true;
    void Promise.all(ids.map((id) => api.getTeamDetail(id).then((d) => d, () => null))).then((results) => {
      if (!active) return;
      const ok = results.filter((d): d is TeamDetailDto => d !== null);
      if (ok.length === 0) return;
      setDetails((m) => {
        const next = { ...m };
        for (const d of ok) next[d.id] = d;
        return next;
      });
      setSelected(
        ok.map((d) => ({
          id: d.id,
          displayName: d.displayName,
          description: d.description,
          memberCount: d.memberCount,
          guestCount: d.guestCount,
          fileCount: d.fileCount,
          lastActivity: d.lastActivity,
          storageUsedBytes: d.storageUsedBytes,
        })),
      );
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function ticketOk(): boolean {
    if (ticketMode === 'Required' && !ticket.trim()) {
      setError('Für diese Aktion ist eine Ticketnummer erforderlich (Governance).');
      return false;
    }
    return true;
  }

  // Optimistisch: die Listen sofort anpassen (Graph-Mitgliedschaft ist erst
  // verzögert konsistent — ein sofortiges Nachladen liefert sonst alten Stand).
  function patch(teamId: string, role: TeamRole, m: GroupMemberDto, add: boolean) {
    setDetails((map) => {
      const d = map[teamId];
      if (!d) return map;
      const pick = role === 'owner' ? d.owners : role === 'guest' ? d.guests : d.members;
      const has = pick.some((x) => x.id === m.id);
      if (add === has) return map; // schon im gewünschten Zustand
      const next: TeamDetailDto = { ...d };
      const apply = (list: GroupMemberDto[]) => (add ? [...list, m] : list.filter((x) => x.id !== m.id));
      if (role === 'owner') next.owners = apply(d.owners);
      else if (role === 'guest') next.guests = apply(d.guests);
      else next.members = apply(d.members);
      const delta = add ? 1 : -1;
      if (role === 'member') next.memberCount = Math.max(0, d.memberCount + delta);
      if (role === 'guest') {
        next.memberCount = Math.max(0, d.memberCount + delta);
        next.guestCount = Math.max(0, d.guestCount + delta);
      }
      return { ...map, [teamId]: next };
    });
  }

  // Hintergrund-Abgleich: erst übernehmen, wenn der Server die Änderung zeigt —
  // so kann ein verzögerter Lesezugriff den optimistischen Stand nicht zurücksetzen.
  async function reconcile(teamId: string, userId: string, shouldBePresent: boolean, role: TeamRole) {
    for (let attempt = 0; attempt < 4; attempt++) {
      await new Promise((r) => setTimeout(r, attempt === 0 ? 1300 : 1500));
      try {
        const d = await api.getTeamDetail(teamId);
        const pick = role === 'owner' ? d.owners : role === 'guest' ? d.guests : d.members;
        if (pick.some((m) => m.id === userId) === shouldBePresent) {
          setDetails((map) => ({ ...map, [teamId]: d }));
          return;
        }
      } catch {
        /* optimistischen Stand behalten */
      }
    }
  }

  async function addRole(role: TeamRole, user: DirectoryUserDto | undefined) {
    if (!user || selected.length === 0) return;
    if (!ticketOk()) return;
    const m: GroupMemberDto = {
      id: user.id,
      displayName: user.displayName,
      subtitle: user.userPrincipalName,
      kind: 'user',
      nestedCount: null,
      isGuest: role === 'guest',
    };
    setBusy(true);
    setError(null);
    setPending(null);
    try {
      let anyPending = false;
      for (const t of selected) {
        const res = await api.addTeamMember(t.id, {
          userId: user.id,
          userDisplay: user.displayName,
          role,
          ticketNumber: ticket.trim() || undefined,
        });
        if (res.status === 'PendingApproval') {
          anyPending = true;
          continue;
        }
        patch(t.id, role, m, true);
        void reconcile(t.id, user.id, true, role);
      }
      if (anyPending) {
        setPending('Mindestens eine Änderung ist freigabepflichtig und wurde als Antrag eingereicht („Meine Anfragen").');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(team: TeamDetailDto, member: GroupMemberDto, role: TeamRole) {
    if (!ticketOk()) return;
    setBusy(true);
    setError(null);
    setPending(null);
    try {
      const res = await api.removeTeamMember(team.id, member.id, {
        userDisplay: member.displayName,
        role,
        ticketNumber: ticket.trim() || undefined,
      });
      if (res.status === 'PendingApproval') {
        setPending('Die Entfernung ist freigabepflichtig und wurde als Antrag eingereicht („Meine Anfragen").');
        return;
      }
      patch(team.id, role, member, false);
      void reconcile(team.id, member.id, false, role);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Teams anpassen"
        description="Teams links wählen — Owner, Mitglieder und Gäste hinzufügen oder entfernen. Speicherplatz und Nutzung je Team."
      />

      {error ? (
        <div style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        </div>
      ) : null}
      {pending ? (
        <div style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>Freigabe nötig</MessageBarTitle>
              {pending}
            </MessageBarBody>
          </MessageBar>
        </div>
      ) : null}

      <div className="grp-layout">
        <TeamList selected={selectedIds} onToggle={toggle} />

        <div className="grp-detail-stack">
          {selected.length === 0 ? (
            <div className="grp-panel">
              <div className="grp-empty">Wähle links ein oder mehrere Teams, um Mitglieder zu bearbeiten.</div>
            </div>
          ) : (
            <>
              <div className="grp-panel">
                <span className="grp-panel-title">Mitglieder hinzufügen</span>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  {selected.length === 1
                    ? `Zum Team „${selected[0].displayName}".`
                    : `Zu ${selected.length} gewählten Teams gleichzeitig.`}
                  {approvalRequired ? ' Änderungen sind freigabepflichtig.' : ''}
                </Caption1>

                <Field label="Owner">
                  <UserPicker onSelect={(u) => addRole('owner', u)} placeholder="Person suchen und als Owner hinzufügen…" clearOnSelect />
                </Field>
                <Field label="Benutzer (Mitglied)">
                  <UserPicker onSelect={(u) => addRole('member', u)} placeholder="Person suchen und als Mitglied hinzufügen…" clearOnSelect />
                </Field>
                <Field label="Gäste (externe Konten)" hint="Listet nur Gastkonten (B2B/extern).">
                  <UserPicker onSelect={(u) => addRole('guest', u)} placeholder="Gastkonto suchen und hinzufügen…" clearOnSelect guestsOnly />
                </Field>

                {ticketMode !== 'None' ? (
                  <Field
                    label="Ticketnummer"
                    required={ticketMode === 'Required'}
                    hint={ticketMode === 'Required' ? 'Pflicht für Mitglieder-Änderungen.' : 'Optional.'}
                  >
                    <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. REQ0012345" />
                  </Field>
                ) : null}

                {busy ? <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Wird übernommen…</Caption1> : null}
              </div>

              {selected.map((t) => {
                const detail = details[t.id];
                if (!detail) {
                  return (
                    <div className="grp-panel" key={t.id}>
                      <div className="grp-panel-head">
                        <span className="grp-panel-title">{t.displayName}</span>
                      </div>
                      {loadingIds.has(t.id) ? <Spinner size="tiny" label="Lade Details…" /> : null}
                    </div>
                  );
                }
                return <TeamEditCard key={t.id} detail={detail} busy={busy} onRemove={(m, role) => removeMember(detail, m, role)} />;
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
